import { useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/lib/auth';
import { usePrefs, ACCENTS, DENSITIES } from '@/lib/prefs';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Check, Sparkles, Palette, Smile, ZoomIn, LayoutPanelLeft,
  Sun, Moon, Monitor, Bell, Clock, Languages, LayoutDashboard,
} from 'lucide-react';

const HOME_FOCUS = [
  { id: 'commitments', label: 'Commitments', hint: 'See your open promises first.' },
  { id: 'projects',    label: 'Projects',    hint: 'Lead with your active projects.' },
  { id: 'inbox',       label: 'Inbox',       hint: 'Notifications and requests up top.' },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const { prefs, patch } = usePrefs();
  const [tab, setTab] = useState('theme');

  const setAccent = async (id) => { await patch({ accent: id }); toast.success('Accent updated'); };
  const setDensity = async (id) => { await patch({ density: id }); toast.success(`Density: ${id}`); };
  const setZoom = async (v) => { await patch({ zoom: v }); };
  const setTheme = async (t) => { await patch({ theme: t }); toast.success(`Theme: ${t}`); };
  const setStatus = async (emoji, text) => {
    await patch({ status_emoji: emoji, status_text: text }); toast.success('Status saved');
  };
  const setLanguage = async (style) => {
    await patch({ language_style: style });
    toast.success(style === 'simple' ? 'Simple language enabled — team-facing screens will use plainer copy.' : 'Standard language restored.');
  };
  const setNotification = async (key, val) => {
    await patch({ notification_prefs: { [key]: val } });
  };
  const setReminderTime = async (key, val) => {
    await patch({ reminder_times: { [key]: val } });
  };
  const setHomeFocus = async (id) => {
    await patch({ layout_prefs: { home_focus: id } });
    toast.success(`Home focus: ${id}`);
  };

  return (
    <AppShell breadcrumbs={[{ label: 'Settings' }]}>
      <div className="p-6 max-w-[960px] mx-auto space-y-5 fade-up" data-testid="settings-page">
        <div>
          <h1 className="font-heading text-3xl font-semibold">Personalize your workspace</h1>
          <p className="text-[hsl(var(--foreground-muted))] text-[14px] mt-1 max-w-[640px]">
            Small touches that make 32DECEMBER OS feel like yours — and one big switch (Simple language) that plainens the copy on team-facing screens.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="settings-tabs">
            <TabsTrigger value="theme" data-testid="settings-tab-theme"><Palette size={13} className="mr-1" />Theme</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="settings-tab-notifications"><Bell size={13} className="mr-1" />Notifications</TabsTrigger>
            <TabsTrigger value="reminders" data-testid="settings-tab-reminders"><Clock size={13} className="mr-1" />Reminders</TabsTrigger>
            <TabsTrigger value="layout" data-testid="settings-tab-layout"><LayoutDashboard size={13} className="mr-1" />Layout</TabsTrigger>
            <TabsTrigger value="language" data-testid="settings-tab-language"><Languages size={13} className="mr-1" />Language</TabsTrigger>
            <TabsTrigger value="status" data-testid="settings-tab-status"><Smile size={13} className="mr-1" />Status</TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === 'theme' && (
          <div className="space-y-4">
            <SectionCard icon={Sun} title="Theme" hint="Light, dark, or match your OS.">
              <div className="grid grid-cols-3 gap-2" data-testid="settings-theme-choices">
                <ThemeChoice active={prefs.theme === 'light'} onClick={() => setTheme('light')} label="Light" icon={Sun} testId="settings-theme-light" />
                <ThemeChoice active={prefs.theme === 'dark'} onClick={() => setTheme('dark')} label="Dark" icon={Moon} testId="settings-theme-dark" />
                <ThemeChoice active={prefs.theme === 'system'} onClick={() => setTheme('system')} label="System" icon={Monitor} testId="settings-theme-system" />
              </div>
            </SectionCard>

            <SectionCard icon={Palette} title="Accent colour" hint="Applied to primary buttons, active nav, callouts, and links.">
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="settings-accents">
                {ACCENTS.map(a => {
                  const active = prefs.accent === a.id;
                  return (
                    <button key={a.id} onClick={() => setAccent(a.id)}
                            data-testid={`settings-accent-${a.id}`}
                            className={`group flex flex-col items-center gap-2 rounded-xl border p-3 transition-all ${active ? 'ring-2 ring-offset-2' : 'hover:border-[hsl(var(--border-strong))]'}`}
                            style={{ borderColor: 'hsl(var(--border))', ['--tw-ring-color']: a.hex }}>
                      <div className="w-10 h-10 rounded-lg shadow-sm flex items-center justify-center" style={{ background: a.hex }}>
                        {active && <Check size={18} className="text-white" />}
                      </div>
                      <span className="text-[11.5px] font-medium">{a.label}</span>
                    </button>
                  );
                })}
              </div>
            </SectionCard>

            <SectionCard icon={LayoutPanelLeft} title="Density" hint="Choose how tight or airy your workspace feels.">
              <div className="grid grid-cols-3 gap-2" data-testid="settings-density">
                {DENSITIES.map(d => {
                  const active = prefs.density === d.id;
                  return (
                    <button key={d.id} onClick={() => setDensity(d.id)}
                            data-testid={`settings-density-${d.id}`}
                            className={`rounded-xl border p-4 text-left transition-all ${active ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))]' : 'hover:border-[hsl(var(--border-strong))]'}`}
                            style={{ borderColor: active ? 'hsl(var(--primary))' : 'hsl(var(--border))' }}>
                      <div className="font-heading font-semibold">{d.label}</div>
                      <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-1">
                        {d.id === 'compact' ? 'Tighter paddings. More on-screen at once.' : d.id === 'cozy' ? 'The balanced default.' : 'Extra breathing room. Easier on the eyes.'}
                      </div>
                    </button>
                  );
                })}
              </div>
            </SectionCard>

            <SectionCard icon={ZoomIn} title="Content zoom" hint="Scale text and content in Knowledge and page canvases.">
              <div className="flex items-center gap-4" data-testid="settings-zoom">
                <span className="text-[12px] font-mono text-[hsl(var(--foreground-muted))] w-12">85%</span>
                <Slider min={85} max={125} step={5} value={[prefs.zoom || 100]} onValueChange={(v) => setZoom(v[0])} className="flex-1" data-testid="settings-zoom-slider" />
                <span className="text-[12px] font-mono text-[hsl(var(--foreground-muted))] w-12 text-right">125%</span>
                <div className="w-14 text-center font-heading text-lg font-semibold">{prefs.zoom}%</div>
              </div>
            </SectionCard>
          </div>
        )}

        {tab === 'notifications' && (
          <SectionCard icon={Bell} title="Notifications" hint="Choose what pings you. All settings are per-user.">
            <div className="space-y-3">
              <PrefRow label="Email nudges" hint="Approval requests, template decisions, escalations." testId="pref-nudge-email"
                       checked={!!prefs.notification_prefs?.nudge_email}
                       onChange={v => setNotification('nudge_email', v)} />
              <PrefRow label="Daily digest email" hint="One 7am summary of yesterday and today." testId="pref-digest-daily"
                       checked={!!prefs.notification_prefs?.digest_daily}
                       onChange={v => setNotification('digest_daily', v)} />
              <PrefRow label="Wednesday Alignment brief" hint="Weekly team brief; leads only receive the team roll-up." testId="pref-digest-wed"
                       checked={!!prefs.notification_prefs?.digest_wednesday}
                       onChange={v => setNotification('digest_wednesday', v)} />
              <PrefRow label="Mute low-priority toasts" hint="Turn off pop-up toasts for informational events; keep the alert center intact." testId="pref-toast-muted"
                       checked={!!prefs.notification_prefs?.toast_muted_low_priority}
                       onChange={v => setNotification('toast_muted_low_priority', v)} />
            </div>
          </SectionCard>
        )}

        {tab === 'reminders' && (
          <SectionCard icon={Clock} title="Your reminder times" hint="When the studio pings you. Times are your local timezone.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TimeRow label="End-of-day timesheet reminder"
                       hint="Nudge if your daily hours aren't logged."
                       value={prefs.reminder_times?.timesheet_end_of_day_hhmm || '17:30'}
                       onChange={v => setReminderTime('timesheet_end_of_day_hhmm', v)}
                       testId="reminder-timesheet" />
              <TimeRow label="Morning commitments digest"
                       hint="A tap-to-open summary of today's commitments."
                       value={prefs.reminder_times?.commitment_morning_hhmm || '09:15'}
                       onChange={v => setReminderTime('commitment_morning_hhmm', v)}
                       testId="reminder-commitments" />
            </div>
          </SectionCard>
        )}

        {tab === 'layout' && (
          <SectionCard icon={LayoutDashboard} title="Home layout" hint="Which panel leads on your Home page.">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="settings-home-focus">
              {HOME_FOCUS.map(h => {
                const active = (prefs.layout_prefs?.home_focus || 'commitments') === h.id;
                return (
                  <button key={h.id} onClick={() => setHomeFocus(h.id)}
                          data-testid={`settings-home-focus-${h.id}`}
                          className={`rounded-xl border p-4 text-left transition-all ${active ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))]' : 'hover:border-[hsl(var(--border-strong))]'}`}
                          style={{ borderColor: active ? 'hsl(var(--primary))' : 'hsl(var(--border))' }}>
                    <div className="font-heading font-semibold">{h.label}</div>
                    <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-1">{h.hint}</div>
                  </button>
                );
              })}
            </div>
          </SectionCard>
        )}

        {tab === 'language' && (
          <SectionCard icon={Languages} title="Language style" hint="Standard is our default studio voice. Simple plainens copy on team-facing screens (timesheet, gate checklists, wizard, notifications, onboarding).">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="settings-language-choices">
              <LangChoice active={(prefs.language_style || 'standard') === 'standard'}
                          onClick={() => setLanguage('standard')}
                          label="Standard"
                          example="Log time daily. Under 60 seconds. Non-project time (admin, training) counts too."
                          testId="settings-lang-standard" />
              <LangChoice active={prefs.language_style === 'simple'}
                          onClick={() => setLanguage('simple')}
                          label="Simple"
                          example="Add today's hours. It takes 30 seconds. Meetings and training also count."
                          testId="settings-lang-simple" />
            </div>
            <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))] mt-3">
              Simple applies to team-facing screens only in this rollout. The rest of the app stays on Standard so operational language remains consistent.
            </div>
          </SectionCard>
        )}

        {tab === 'status' && (
          <SectionCard icon={Smile} title="Your status" hint="A tiny emoji + line teammates see next to your name.">
            <StatusEditor initialEmoji={prefs.status_emoji} initialText={prefs.status_text} onSave={setStatus} />
          </SectionCard>
        )}

        <div className="card p-5" data-testid="settings-signed-in">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: 'hsl(var(--accent))', color: 'hsl(var(--accent-foreground))' }}><Sparkles size={18} /></div>
            <div>
              <div className="font-heading font-semibold">Signed in as</div>
              <div className="text-[13.5px] text-[hsl(var(--foreground-muted))]">{user.name} · <span className="font-mono">{user.email}</span></div>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

const SectionCard = ({ icon: Icon, title, hint, children }) => (
  <section className="card p-5">
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: 'hsl(var(--surface-2))', color: 'hsl(var(--foreground-muted))' }}><Icon size={17} /></div>
        <div>
          <div className="font-heading font-semibold">{title}</div>
          <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">{hint}</div>
        </div>
      </div>
    </div>
    {children}
  </section>
);

function ThemeChoice({ active, onClick, label, icon: Icon, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
            className={`rounded-xl border p-4 flex items-center gap-3 transition-all ${active ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))]' : 'hover:border-[hsl(var(--border-strong))]'}`}
            style={{ borderColor: active ? 'hsl(var(--primary))' : 'hsl(var(--border))' }}>
      <Icon size={18} className={active ? 'text-[hsl(var(--primary))]' : 'text-[hsl(var(--foreground-muted))]'} />
      <span className="font-heading font-semibold">{label}</span>
      {active && <Check size={14} className="ml-auto text-[hsl(var(--primary))]" />}
    </button>
  );
}

function LangChoice({ active, onClick, label, example, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
            className={`rounded-xl border p-4 text-left transition-all ${active ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))]' : 'hover:border-[hsl(var(--border-strong))]'}`}
            style={{ borderColor: active ? 'hsl(var(--primary))' : 'hsl(var(--border))' }}>
      <div className="flex items-center justify-between">
        <div className="font-heading font-semibold">{label}</div>
        {active && <Check size={14} className="text-[hsl(var(--primary))]" />}
      </div>
      <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] italic mt-2">&ldquo;{example}&rdquo;</div>
    </button>
  );
}

function PrefRow({ label, hint, checked, onChange, testId }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b last:border-b-0" style={{ borderColor: 'hsl(var(--border))' }}>
      <div className="min-w-0">
        <div className="font-medium text-[13.5px]">{label}</div>
        <div className="text-[12px] text-[hsl(var(--foreground-muted))]">{hint}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

function TimeRow({ label, hint, value, onChange, testId }) {
  return (
    <div>
      <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">{label}</div>
      <Input type="time" value={value} onChange={e => onChange(e.target.value)} data-testid={testId} />
      <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))] mt-1">{hint}</div>
    </div>
  );
}

const EMOJI_PRESETS = ['🎨', '🧐', '📚', '☕', '🌿', '🔥', '🔭', '👁‍🗨', '🌙', '⚡', '🛠️', '💬'];
function StatusEditor({ initialEmoji, initialText, onSave }) {
  const [emoji, setEmoji] = useState(initialEmoji || '');
  const [text, setText] = useState(initialText || '');
  return (
    <div>
      <div className="flex flex-wrap gap-1 mb-3" data-testid="settings-status-emoji-palette">
        <button onClick={() => setEmoji('')} className={`w-8 h-8 rounded-md border ${!emoji ? 'ring-2 ring-[hsl(var(--primary))]' : 'hover:bg-[hsl(var(--surface-2))]'} text-[13px]`} style={{ borderColor: 'hsl(var(--border))' }}>∅</button>
        {EMOJI_PRESETS.map(e => (
          <button key={e} onClick={() => setEmoji(e)} className={`w-8 h-8 rounded-md border text-[16px] leading-none ${emoji === e ? 'ring-2 ring-[hsl(var(--primary))]' : 'hover:bg-[hsl(var(--surface-2))]'}`} style={{ borderColor: 'hsl(var(--border))' }} data-testid={`settings-emoji-${e}`}>{e}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <Input placeholder="Deep in Concept 3 — back at 4pm" value={text} onChange={e => setText(e.target.value)} className="flex-1" maxLength={80} data-testid="settings-status-text" />
        <Button onClick={() => onSave(emoji, text)} data-testid="settings-status-save">Save</Button>
      </div>
    </div>
  );
}
